import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useActiveWorld } from '../hooks/useActiveWorld';
import { failedAgentTags } from './outreachCompletedTag';
import './NewWorldDbStatusFilter.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All', fa: 'fa-solid fa-border-all', description: 'Show all contacts', group: 'general' },
  { value: 'my_tasks', label: 'My Tasks', fa: 'fa-solid fa-bolt', faColor: '#e0913a', description: 'Contacts with an open task for you', group: 'general' },
  { value: 'favorites', label: 'Favorites', fa: 'fa-solid fa-heart', faColor: '#ef4444', description: 'Your Favorite Contacts', group: 'general' },
  { value: 'ready_to_spy', label: 'Needs Research', fa: 'fa-solid fa-magnifying-glass', faColor: '#f59e0b', description: 'No Email Available', group: 'general' },
  { value: 'prepared_email', label: 'Prepared', fa: 'fa-solid fa-wand-magic-sparkles', faColor: '#10b981', description: 'Flow email pre-generated — ready to send', group: 'general' },
  { value: 'Not Contacted', label: 'Not Contacted', fa: 'fa-solid fa-circle', faColor: '#94a3b8', description: 'No email sent yet', group: 'pipeline' },
  { value: 'Lead', label: 'Lead', fa: 'fa-solid fa-bullseye', faColor: '#3b82f6', description: 'Qualified as lead', group: 'pipeline' },
  { value: 'In Outreach', label: 'In Outreach', fa: 'fa-solid fa-satellite-dish', faColor: '#6366f1', description: 'Currently in an outreach job', group: 'pipeline' },
  { value: 'Introduced', label: 'Introduced', fa: 'fa-solid fa-paper-plane', faColor: '#8b5cf6', description: 'Intro email sent', group: 'outreach' },
  { value: 'First Follow-up', label: '1. Follow-Up', fa: 'fa-solid fa-reply', faColor: '#a78bfa', description: '1st follow-up sent', group: 'outreach' },
  { value: 'Second Follow-up', label: '2. Follow-Up', fa: 'fa-solid fa-reply-all', faColor: '#c4b5fd', description: '2nd follow-up sent', group: 'outreach' },
  { value: 'Third Follow-up', label: '3. Follow-Up', fa: 'fa-solid fa-forward', faColor: '#ddd6fe', description: '3rd follow-up sent', group: 'outreach' },
  { value: 'replied', label: 'Replied', fa: 'fa-solid fa-comment-dots', faColor: '#10b981', description: 'Contact replied — excluded from follow-ups', group: 'result' },
  { value: 'Interested', label: 'Interested', fa: 'fa-solid fa-circle-check', faColor: '#22c55e', description: 'Positive reply received', group: 'result' },
  { value: 'Not Interested', label: 'Not Interested', fa: 'fa-solid fa-circle-xmark', faColor: '#ef4444', description: 'Declined or unsubscribed', group: 'result' },
  { value: 'Outreach: Removed', label: 'Outreach: Removed', fa: 'fa-solid fa-user-minus', faColor: '#f59e0b', description: 'Removed or skipped from job', group: 'result' },
  { value: 'Outreach: Failed', label: 'Outreach: Failed', fa: 'fa fa-exclamation-triangle', faColor: '#ef4444', description: 'Job processing failed', group: 'result' },
];

const GROUP_LABELS = {
  general: null,
  pipeline: 'Pipeline',
  outreach: 'Outreach Stages',
  result: 'Results',
};

const GROUPS_ORDER = ['general', 'pipeline', 'outreach', 'result'];

const NewWorldDbStatusFilter = ({ onFilterChange, isLoading, currentStatusFilter }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const { userId } = useActiveWorld();
  const [jobs, setJobs] = useState({});

  useEffect(() => {
    if (!userId) return undefined;
    const jobsRef = ref(database, `World/${userId}/settings/outreachJobs`);
    const unsub = onValue(jobsRef, (snap) => {
      setJobs(snap.exists() ? snap.val() || {} : {});
    });
    return () => unsub();
  }, [userId]);

  const agentFailOptions = useMemo(() => {
    const seen = new Set();
    return failedAgentTags(jobs).filter((tag) => {
      if (seen.has(tag.tag)) return false;
      seen.add(tag.tag);
      return true;
    }).map((tag) => ({
      value: tag.tag,
      label: tag.tag,
      fa: 'fa fa-exclamation-triangle',
      faColor: '#ef4444',
      description: 'Dropped out of this agent — left the queue',
      group: 'result',
    }));
  }, [jobs]);

  const allOptions = useMemo(() => [...STATUS_OPTIONS, ...agentFailOptions], [agentFailOptions]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const current = allOptions.find(o => o.value === currentStatusFilter) || STATUS_OPTIONS[3];

  const handleSelect = (value) => {
    onFilterChange(value);
    setOpen(false);
  };

  const grouped = GROUPS_ORDER.map(groupKey => ({
    key: groupKey,
    label: GROUP_LABELS[groupKey],
    items: allOptions.filter(o => o.group === groupKey),
  }));

  return (
    <div className="wdsf-wrapper" ref={wrapperRef}>
      <button
        className={`wdsf-trigger ${open ? 'wdsf-trigger-open' : ''} ${current.value !== 'all' ? 'wdsf-trigger-active' : ''}`}
        onClick={() => !isLoading && setOpen(p => !p)}
        disabled={isLoading}
        title="Filter by status"
      >
        {current.fa && <i className={`${current.fa} wdsf-trigger-icon`} style={current.faColor ? { color: current.faColor } : {}} />}
        <span className="wdsf-trigger-label">{current.label}</span>
        <svg className={`wdsf-trigger-chevron ${open ? 'wdsf-trigger-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="wdsf-dropdown">
          {grouped.map((group, gi) => (
            <React.Fragment key={group.key}>
              {group.label && (
                <div className="wdsf-group-label">{group.label}</div>
              )}
              {group.items.map(opt => {
                const active = opt.value === currentStatusFilter;
                return (
                  <div
                    key={opt.value}
                    className={`wdsf-option ${active ? 'wdsf-option-selected' : ''}`}
                    onClick={() => handleSelect(opt.value)}
                  >
                    <span className={`wdsf-radio ${active ? 'wdsf-radio-active' : ''}`} />
                    <i className={`${opt.fa} wdsf-option-fa`} style={opt.faColor ? { color: opt.faColor } : {}} />
                    <div className="wdsf-option-content">
                      <span className="wdsf-option-label">{opt.label}</span>
                      <span className="wdsf-option-desc">{opt.description}</span>
                    </div>
                  </div>
                );
              })}
              {gi < grouped.length - 1 && <div className="wdsf-divider" />}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewWorldDbStatusFilter;
